/* ─────────────────────────────────────────────────────────────
   TSG Published-At Sort Engine
   assets/tsg-published-sort.js

   Purpose: Shopify's native "Date, new to old" sorts by created_at.
   Because Mortar creates products as drafts (created_at ≠ go-live),
   that buries recently-activated items. This engine re-sorts the
   collection grid by TRUE published_at whenever the effective sort
   is created-descending.

   How:
     • Single-page collections  → reorder the cards already in the DOM.
     • Multi-page collections   → fetch every page's real Dawn cards via
       the Section Rendering API, sort the full set by published_at,
       and paginate client-side (page 1 = newest across the WHOLE set).
     • Uses Dawn's own server-rendered cards, so metafield pills
       (serial / weight / wood) are preserved.
     • A MutationObserver re-runs the sort after Dawn's AJAX filtering
       or pagination re-renders the grid.
     • Engages ONLY when the effective sort is created-descending.
       Every other sort is left untouched.
     • Any fetch/parse failure falls back to Dawn's native order —
       never an empty grid.

   Targets ul#product-grid.product-grid (Dawn collection grid). Search
   pages use #product-grid on a wrapper div, not the <ul>, so this
   no-ops there by design.
──────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var TARGET_SORT = 'created-descending';
  var CONTAINER   = '#ProductGridContainer';

  var store    = {}; // rebuildKey -> { items: [sorted <li> nodes], perPage }
  var observer = null;

  /* ── Element + value helpers ─────────────────────────────── */

  function grid() {
    return document.querySelector('ul#product-grid.product-grid');
  }

  function effectiveSort(ul) {
    var param = new URLSearchParams(window.location.search).get('sort_by');
    return param || ul.getAttribute('data-tsg-default-sort') || '';
  }

  function publishedTs(li) {
    var v = li.getAttribute('data-published-at');
    return v ? (parseInt(v, 10) || 0) : 0;
  }

  function sortNodes(nodes) {
    nodes.sort(function (a, b) { return publishedTs(b) - publishedTs(a); });
    return nodes;
  }

  function currentPageNum() {
    return parseInt(new URLSearchParams(window.location.search).get('page'), 10) || 1;
  }

  /* Params minus page — identifies a filtered set independent of page */
  function baseParams() {
    var p = new URLSearchParams(window.location.search);
    p.delete('page');
    return p;
  }

  function rebuildKey() {
    return window.location.pathname + '?' + baseParams().toString();
  }

  /* Run a DOM-writing fn without the observer re-triggering us */
  function paused(fn) {
    var c = document.querySelector(CONTAINER);
    if (observer) observer.disconnect();
    try { fn(); }
    finally { if (observer && c) observer.observe(c, { childList: true, subtree: true }); }
  }

  /* ── Single page: reorder what's already rendered ────────── */

  function reorderInPlace(ul) {
    var items = Array.prototype.filter.call(ul.children, function (n) {
      return n.classList && n.classList.contains('grid__item');
    });
    if (items.length < 2) return;
    sortNodes(items);
    paused(function () {
      var frag = document.createDocumentFragment();
      items.forEach(function (li) { frag.appendChild(li); }); // moves existing nodes
      ul.appendChild(frag);
    });
  }

  /* ── Multi page: fetch all pages, sort, client-paginate ──── */

  function fetchPage(sectionId, pageNum) {
    var p = baseParams();
    p.set('section_id', sectionId);
    p.set('page', pageNum);
    return fetch(window.location.pathname + '?' + p.toString())
      .then(function (r) {
        if (!r.ok) throw new Error('section fetch ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.prototype.slice.call(doc.querySelectorAll('.grid__item'));
      });
  }

  function multiPage(ul) {
    var sectionId  = ul.getAttribute('data-id');
    var totalPages = parseInt(ul.getAttribute('data-tsg-total-pages'), 10) || 1;
    var perPage    = parseInt(ul.getAttribute('data-tsg-per-page'), 10) || 24;
    if (!sectionId) return;

    var key = rebuildKey();

    /* Already rebuilt this filtered set — just show the requested page */
    if (ul.getAttribute('data-tsg-key') === key && store[key]) {
      renderClientPage(ul, key);
      return;
    }

    ul.style.opacity = '0.4';

    var jobs = [];
    for (var i = 1; i <= totalPages; i++) jobs.push(fetchPage(sectionId, i));

    Promise.all(jobs)
      .then(function (pages) {
        var all = [];
        pages.forEach(function (nodes) {
          nodes.forEach(function (n) { all.push(document.importNode(n, true)); });
        });
        sortNodes(all);
        store[key] = { items: all, perPage: perPage };
        ul.setAttribute('data-tsg-key', key); // attribute change → observer ignores it
        renderClientPage(ul, key);
        ul.style.opacity = '';
      })
      .catch(function (err) {
        ul.style.opacity = '';
        if (window.console) console.warn('[TSG sort] rebuild failed — native order kept:', err);
      });
  }

  function renderClientPage(ul, key) {
    var data = store[key];
    if (!data) return;

    var total      = data.items.length;
    var perPage    = data.perPage;
    var totalPages = Math.max(1, Math.ceil(total / perPage));
    var page       = Math.min(Math.max(currentPageNum(), 1), totalPages);
    var start      = (page - 1) * perPage;
    var slice      = data.items.slice(start, start + perPage);

    paused(function () {
      var frag = document.createDocumentFragment();
      slice.forEach(function (li) { frag.appendChild(li.cloneNode(true)); }); // keep originals for other pages
      ul.innerHTML = '';
      ul.appendChild(frag);
      rebuildPagination(ul, page, totalPages);
    });
  }

  /* ── Client pagination nav (mirrors Dawn's markup/classes) ── */

  function pageHref(n) {
    var p = new URLSearchParams(window.location.search);
    if (n <= 1) p.delete('page'); else p.set('page', n);
    var qs = p.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function buildNav(current, total) {
    if (total <= 1) return '';
    var out = ['<nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled">'];

    if (current > 1) {
      out.push('<li><a href="' + pageHref(current - 1) + '" class="pagination__item pagination__item--prev pagination__item-arrow link" data-tsg-page="' + (current - 1) + '" aria-label="Previous page">&lsaquo;</a></li>');
    }

    var seq = [];
    for (var i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - 2 && i <= current + 2)) seq.push(i);
      else if (seq[seq.length - 1] !== '…') seq.push('…');
    }
    seq.forEach(function (i) {
      if (i === '…') out.push('<li><span class="pagination__item">…</span></li>');
      else if (i === current) out.push('<li><span class="pagination__item pagination__item--current" aria-current="page">' + i + '</span></li>');
      else out.push('<li><a href="' + pageHref(i) + '" class="pagination__item link" data-tsg-page="' + i + '">' + i + '</a></li>');
    });

    if (current < total) {
      out.push('<li><a href="' + pageHref(current + 1) + '" class="pagination__item pagination__item-arrow link" data-tsg-page="' + (current + 1) + '" aria-label="Next page">&rsaquo;</a></li>');
    }

    out.push('</ul></nav>');
    return out.join('');
  }

  function rebuildPagination(ul, current, total) {
    var container = ul.parentNode; // Dawn's .collection wrapper
    if (!container) return;

    var wrapper = container.querySelector('.pagination-wrapper');
    var navHtml = buildNav(current, total);

    if (wrapper) {
      wrapper.innerHTML = navHtml;
    } else if (navHtml) {
      wrapper = document.createElement('div');
      wrapper.className = 'pagination-wrapper';
      wrapper.innerHTML = navHtml;
      container.appendChild(wrapper);
    }
    bindPaginationClicks(ul, container);
  }

  function bindPaginationClicks(ul, container) {
    container.querySelectorAll('[data-tsg-page]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var n = parseInt(link.getAttribute('data-tsg-page'), 10) || 1;
        history.pushState({}, '', pageHref(n));
        renderClientPage(ul, rebuildKey());
        var anchor = document.querySelector(CONTAINER) || ul;
        window.scrollTo({
          top: anchor.getBoundingClientRect().top + window.pageYOffset - 100,
          behavior: 'smooth'
        });
      });
    });
  }

  /* ── Orchestration ───────────────────────────────────────── */

  function run() {
    var ul = grid();
    if (!ul) return;

    if (effectiveSort(ul) !== TARGET_SORT) {
      /* Not our sort. If we'd previously taken over this UL, hand it
         back to the server's native order for the new sort. */
      if (ul.getAttribute('data-tsg-key')) window.location.reload();
      return;
    }

    var totalPages = parseInt(ul.getAttribute('data-tsg-total-pages'), 10) || 1;
    if (totalPages <= 1) reorderInPlace(ul);
    else multiPage(ul);
  }

  var scheduled = false;
  function scheduleRun() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; run(); });
  }

  function observe() {
    var c = document.querySelector(CONTAINER);
    if (!c) return;
    observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'childList') { scheduleRun(); return; }
      }
    });
    observer.observe(c, { childList: true, subtree: true });
  }

  window.addEventListener('popstate', function () {
    var ul = grid();
    if (!ul) return;
    var key = rebuildKey();
    if (store[key]) renderClientPage(ul, key);
    else run();
  });

  function init() {
    if (!grid()) return; // only collection grids
    observe();
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();